/**
 * Resumable S3 multipart upload for one source file (contract §4.5 + amendments 2 and 6):
 *   POST /sessions/{id}/uploads → { source_id, upload_id, part_size, part_count, part_urls (first 200 parts), expires_in_s }
 *   POST /sessions/{id}/uploads/{source_id}/parts { parts } → more URLs, in batches, and fresh ones before the ~15-min expiry
 *   PUT each part to its URL (4 in parallel), keep each ETag
 *   POST /sessions/{id}/uploads/{source_id}/complete { upload_id, parts: [{ n, etag }] }
 * On resume, GET /sessions/{id}/uploads/{source_id} says which parts the server already holds (the source of truth);
 * the local ledger (plan.ts) only remembers the source id and upload id across reloads.
 */
import { api } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import type { CompletedPart, SourceKind } from '@/lib/api/types';
import { logError, logInfo, logWarn } from '@/lib/log';
import { clearRecord, fingerprint, loadRecord, newRecord, partCount, partRange, pendingParts, saveRecord, bytesDone as ledgerBytes, type ResumeRecord } from './plan';

export interface UploadMeta {
  kind: SourceKind;
  person: string;
  label: string;
  /** Everyone in a camera's frame (two-shot, three-shot …); amendment 11. Omitted when empty. */
  people?: string[];
}

export interface UploadHooks {
  signal: AbortSignal;
  /** Bytes stored so far (completed parts + in-flight progress). */
  onProgress: (bytes: number, total: number) => void;
  /** Called once when an earlier upload is being continued, with the first part still missing. */
  onResume?: (firstPart: number) => void;
  /** Called as soon as the server has assigned a source id (lets the caller abort it later). */
  onStarted?: (sourceId: string) => void;
  concurrency?: number;
  /** Test seam: replace the XHR part uploader. */
  putPart?: PutPart;
  /** Test seam: clock in ms. */
  now?: () => number;
}

export type PutPart = (url: string, body: Blob, signal: AbortSignal, onProgress: (loaded: number) => void) => Promise<string>;

export class UploadAbortedError extends Error {
  constructor() {
    super('Upload paused');
    this.name = 'AbortError';
  }
}

export class PartHttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'PartHttpError';
  }
}

const MAX_ATTEMPTS = 4;
/** URLs are requested in batches of this many parts (the API caps one call at 1000). */
export const URL_BATCH = 200;
/** Refresh a URL when less than this much of its lifetime is left. */
const EXPIRY_MARGIN_MS = 2 * 60_000;
const DEFAULT_TTL_MS = 15 * 60_000;

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new UploadAbortedError());
    }, { once: true });
  });

/** PUT one part with XMLHttpRequest (fetch has no upload progress). Resolves with the part's ETag. */
export const xhrPutPart: PutPart = (url, body, signal, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        if (!etag) {
          reject(new PartHttpError(0, 'Storage did not expose the ETag header. The bucket CORS rule must include ExposeHeaders: ETag.'));
          return;
        }
        resolve(etag);
      } else {
        reject(new PartHttpError(xhr.status, `part upload failed with HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new PartHttpError(0, 'network error while uploading a part'));
    xhr.onabort = () => reject(new UploadAbortedError());
    if (signal.aborted) {
      reject(new UploadAbortedError());
      return;
    }
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });

/** Part URLs with their expiry, fetched lazily in batches (single flight). */
class UrlBook {
  private urls = new Map<number, { url: string; expiresAt: number }>();
  private inflight: Promise<void> | null = null;

  constructor(
    private readonly fetchUrls: (parts: number[]) => Promise<{ part_urls: { n: number; url: string }[]; expires_in_s?: number }>,
    private readonly now: () => number,
    private readonly tag: string,
  ) {}

  add(list: { n: number; url: string }[], ttlS: number | undefined, issuedAt = this.now()) {
    const expiresAt = issuedAt + (ttlS ? ttlS * 1000 : DEFAULT_TTL_MS);
    for (const p of list) this.urls.set(p.n, { url: p.url, expiresAt });
  }

  invalidate(n: number) {
    this.urls.delete(n);
  }

  fresh(n: number): string | null {
    const u = this.urls.get(n);
    return u && u.expiresAt - this.now() > EXPIRY_MARGIN_MS ? u.url : null;
  }

  /** URL for part n; fetches a batch (n and the following pending parts) when missing or about to expire. */
  async get(n: number, upcoming: () => number[]): Promise<string> {
    for (let i = 0; i < 2; i++) {
      const hit = this.fresh(n);
      if (hit) return hit;
      if (!this.inflight) {
        const batch = [n, ...upcoming().filter((p) => p !== n && !this.fresh(p))].slice(0, URL_BATCH);
        this.inflight = this.fetchUrls(batch)
          .then((res) => {
            this.add(res.part_urls, res.expires_in_s);
            logInfo('upload', `${this.tag} fetched ${res.part_urls.length} part URL(s) from part ${batch[0]}`);
          })
          .finally(() => {
            this.inflight = null;
          });
      }
      await this.inflight;
    }
    const hit = this.fresh(n);
    if (!hit) throw new PartHttpError(0, `no upload URL for part ${n}`);
    return hit;
  }
}

export async function uploadFile(sessionId: string, file: File, meta: UploadMeta, hooks: UploadHooks): Promise<{ source_id: string }> {
  const fp = fingerprint(sessionId, file);
  const put = hooks.putPart ?? xhrPutPart;
  const now = hooks.now ?? Date.now;
  const tag = `session=${sessionId} file="${file.name}"`;

  // ── 1. Start, or continue an earlier upload of the same file ────────────
  let rec: ResumeRecord | null = loadRecord(fp);
  let initialUrls: { n: number; url: string }[] = [];
  let ttl: number | undefined;
  if (rec) {
    try {
      const state = await api.pipeline.uploadState(sessionId, rec.source_id);
      if (state.upload_status !== 'pending' || state.upload_id !== rec.upload_id) throw new ApiError(409, `upload is ${state.upload_status}`);
      rec.done = Object.fromEntries(state.parts.map((p) => [p.n, p.etag]));
      rec.part_size = state.part_size;
      saveRecord(fp, rec);
      const first = pendingParts(partCount(rec.size, rec.part_size), rec.done)[0] ?? partCount(rec.size, rec.part_size);
      logInfo('upload', `${tag} resuming source=${rec.source_id} from part ${first} (${state.parts.length} stored)`);
      hooks.onResume?.(first);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 409 || e.status === 410)) {
        logWarn('upload', `${tag} earlier upload source=${rec.source_id} can't be resumed (${e.status}); starting over`);
        clearRecord(fp);
        rec = null;
      } else {
        logError('upload', `${tag} could not read the upload state of source=${rec.source_id}`, e);
        throw e;
      }
    }
  }
  if (!rec) {
    try {
      const plan = await api.pipeline.startUpload(sessionId, { filename: file.name, size: file.size, ...meta, content_type: file.type || undefined });
      rec = newRecord(sessionId, file, meta.kind, { ...plan, part_urls: [] });
      saveRecord(fp, rec);
      initialUrls = plan.part_urls;
      ttl = plan.expires_in_s;
      logInfo('upload', `${tag} started source=${plan.source_id} parts=${plan.part_count ?? partCount(file.size, plan.part_size)} part_size=${plan.part_size}`);
    } catch (e) {
      logError('upload', `${tag} could not start the multipart upload`, e);
      throw e;
    }
  }
  const record = rec;
  hooks.onStarted?.(record.source_id);

  // ── 2. Upload the missing parts ──────────────────────────────────────
  const count = partCount(record.size, record.part_size);
  const book = new UrlBook((parts) => api.pipeline.partUrls(sessionId, record.source_id, parts), now, `${tag} source=${record.source_id}`);
  book.add(initialUrls, ttl);
  const inflight = new Map<number, number>();
  let stored = ledgerBytes(record.size, record.part_size, record.done);
  const report = () => hooks.onProgress(Math.min(record.size, stored + [...inflight.values()].reduce((a, b) => a + b, 0)), record.size);
  report();

  const queue = pendingParts(count, record.done);
  const upcoming = () => queue;
  const uploadPart = async (n: number) => {
    const [a, b] = partRange(n, record.size, record.part_size);
    const blob = file.slice(a, b);
    let refreshed = false;
    for (let attempt = 1; ; attempt++) {
      if (hooks.signal.aborted) throw new UploadAbortedError();
      let url: string;
      try {
        url = await book.get(n, upcoming);
      } catch (e) {
        logError('upload', `${tag} source=${record.source_id} part=${n} could not get an upload URL`, e);
        throw e;
      }
      try {
        const etag = await put(url, blob, hooks.signal, (loaded) => {
          inflight.set(n, loaded);
          report();
        });
        inflight.delete(n);
        record.done[n] = etag;
        stored += b - a;
        saveRecord(fp, record);
        report();
        return;
      } catch (e) {
        inflight.delete(n);
        report();
        if (e instanceof UploadAbortedError || hooks.signal.aborted) throw new UploadAbortedError();
        const status = e instanceof PartHttpError ? e.status : 0;
        if (status === 403 && !refreshed) {
          // Expired or clock-skewed signature: drop the URL and fetch a fresh one.
          refreshed = true;
          logWarn('upload', `${tag} part=${n} got 403 from storage; refreshing its URL`);
          book.invalidate(n);
          continue;
        }
        const retryable = status === 0 || status === 408 || status === 429 || status >= 500;
        if (!retryable || attempt >= MAX_ATTEMPTS || (e instanceof PartHttpError && /ETag/.test(e.message))) {
          logError('upload', `${tag} source=${record.source_id} part=${n} failed after ${attempt} attempt(s) status=${status}`, e);
          throw e;
        }
        const wait = 1000 * 2 ** (attempt - 1);
        logWarn('upload', `${tag} part=${n} attempt ${attempt} failed (status=${status}); retrying in ${wait} ms`);
        await sleep(wait, hooks.signal);
      }
    }
  };

  const workers = Array.from({ length: Math.min(hooks.concurrency ?? 4, Math.max(1, queue.length)) }, async () => {
    for (let n = queue.shift(); n !== undefined; n = queue.shift()) await uploadPart(n);
  });
  await Promise.all(workers);

  // ── 3. Complete ──────────────────────────────────────────────────────
  const parts: CompletedPart[] = Object.entries(record.done)
    .map(([n, etag]) => ({ n: Number(n), etag }))
    .sort((x, y) => x.n - y.n);
  try {
    await api.pipeline.completeUpload(sessionId, record.source_id, { upload_id: record.upload_id, parts });
  } catch (e) {
    // 409 = already completed/aborted server-side: drop the ledger so the next try starts clean.
    if (e instanceof ApiError && (e.status === 404 || e.status === 409)) clearRecord(fp);
    logError('upload', `${tag} source=${record.source_id} complete failed`, e);
    throw e;
  }
  clearRecord(fp);
  logInfo('upload', `${tag} complete source=${record.source_id} parts=${parts.length}`);
  return { source_id: record.source_id };
}

/** Cancel an upload on the server and forget it locally (removing a file from the list). */
export async function abortUpload(sessionId: string, file: File, sourceId: string): Promise<void> {
  clearRecord(fingerprint(sessionId, file));
  try {
    await api.pipeline.abortUpload(sessionId, sourceId);
    logInfo('upload', `aborted session=${sessionId} source=${sourceId}`);
  } catch (e) {
    logError('upload', `abort failed session=${sessionId} source=${sourceId}`, e);
  }
}
